export type ConvertedSample = { url: string; label: string; engine: string; durationSeconds?: number; referenceSynthetic?: boolean }
export type StorySample = {
  id: string
  title: string
  language: string
  description: string
  sourceUrl: string
  sourceLabel: string
  sourcePage: string
  sourceKind: 'human-narration' | 'synthetic-narration' | 'unknown'
  durationSeconds?: number
  converted?: ConvertedSample
}
export type StorySampleManifest = { version: 2; cases: StorySample[] }

const record = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value)
const text = (value: unknown, max: number) => typeof value === 'string' && value.trim().length > 0 && value.length <= max
const duration = (value: unknown) => typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= 21_600
export function sampleSourceLink(value: string): string | null {
  try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : null }
  catch { return null }
}
export function safeSampleUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 500 || !value.startsWith('/assets/') || value.includes('\\')) return false
  try {
    const url = new URL(value, 'https://lulla-samples.invalid')
    const decoded = decodeURIComponent(url.pathname)
    return url.origin === 'https://lulla-samples.invalid' && !url.search && !url.hash
      && (decoded.startsWith('/assets/voice-samples/') || decoded.startsWith('/assets/story-samples/'))
      && !decoded.split('/').slice(1).some(segment => !segment || ['.', '..'].includes(segment))
      && /\.(mp3|wav|m4a|ogg|opus)$/i.test(decoded)
  } catch { return false }
}

/** Original narration is independent of voice conversion; never invent a pair. */
export function parseSampleManifest(value: unknown): StorySampleManifest | null {
  if (!record(value) || !Array.isArray(value.cases) || !value.cases.length || value.cases.length > 50) return null
  if (value.version !== undefined && value.version !== 2) return null
  const cases: StorySample[] = []
  for (const item of value.cases) {
    if (!record(item) || !text(item.id, 80) || !/^[A-Za-z0-9_-]+$/.test(item.id as string) || !text(item.title, 160)
      || !text(item.language, 40) || !text(item.sourceLabel, 240) || typeof item.sourcePage !== 'string'
      || (item.description !== undefined && (typeof item.description !== 'string' || item.description.length > 1_000))) return null
    const modern = value.version === 2
    const sourceUrl = modern ? item.sourceUrl : item.beforeUrl
    if (!safeSampleUrl(sourceUrl) || (modern && (!duration(item.durationSeconds) || !sampleSourceLink(item.sourcePage)))) return null
    const sourceKind = item.sourceKind ?? 'unknown'
    if (!['human-narration', 'synthetic-narration', 'unknown'].includes(sourceKind as string)) return null
    let converted: ConvertedSample | undefined
    const pair = modern ? item.converted : safeSampleUrl(item.afterUrl) ? { url: item.afterUrl, label: item.referenceLabel, engine: value.engine, referenceSynthetic: item.referenceSynthetic } : undefined
    if (pair !== undefined && pair !== null) {
      if (!record(pair) || !safeSampleUrl(pair.url) || pair.url === sourceUrl || !text(pair.label, 240) || !text(pair.engine, 160)
        || (pair.durationSeconds !== undefined && !duration(pair.durationSeconds))
        || (pair.referenceSynthetic !== undefined && typeof pair.referenceSynthetic !== 'boolean')) return null
      converted = { url: pair.url, label: pair.label as string, engine: pair.engine as string, durationSeconds: pair.durationSeconds as number | undefined, referenceSynthetic: pair.referenceSynthetic as boolean | undefined }
    }
    cases.push({ id: item.id as string, title: item.title as string, language: item.language as string, description: item.description as string ?? '', sourceUrl, sourceLabel: item.sourceLabel as string, sourcePage: item.sourcePage, sourceKind: sourceKind as StorySample['sourceKind'], durationSeconds: duration(item.durationSeconds) ? item.durationSeconds as number : undefined, converted })
  }
  return new Set(cases.map(item => item.id)).size === cases.length ? { version: 2, cases } : null
}

export function sampleDuration(seconds: number | undefined) {
  if (!seconds || !Number.isFinite(seconds)) return '재생 길이 확인 중'
  const rounded = Math.round(seconds)
  return `${Math.floor(rounded / 60)}분 ${String(rounded % 60).padStart(2, '0')}초`
}
export function sampleDownloadName(title: string, url: string, converted = false) {
  const extension = new URL(url, 'https://lulla-samples.invalid').pathname.split('.').pop() ?? 'mp3'
  const suffix = `${converted ? '_변환' : ''}.${extension}`
  const base = title.replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_').replace(/\.{2,}/g, '_').replace(/^\./, '_').trim() || '동화'
  return `${base.slice(0, 120 - suffix.length).trim()}${suffix}`
}
